import React from "react";
import { FileText, Image, File, Download, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface FileMessageProps {
  fileUrl: string;
  fileName: string;
  fileType: string;
  fileSize: number;
}

export function FileMessage({ fileUrl, fileName, fileType, fileSize }: FileMessageProps) {
  const isImage = fileType.startsWith('image/');
  const isPdf = fileType === 'application/pdf';

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = () => {
    if (isImage) return <Image className="h-5 w-5 text-blue-500" />;
    if (isPdf) return <FileText className="h-5 w-5 text-red-500" />;
    return <File className="h-5 w-5 text-muted-foreground" />;
  };

  const handleDownload = () => {
    window.open(fileUrl, '_blank');
  };

  if (isImage) {
    return (
      <div className="space-y-2">
        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={fileUrl}
            alt={fileName}
            className="max-w-[300px] max-h-[300px] rounded-lg object-contain cursor-pointer hover:opacity-90 transition-opacity"
          />
        </a>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate max-w-[200px]">{fileName}</span>
          <span>({formatFileSize(fileSize)})</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 bg-muted/50 border border-border rounded-lg p-3 max-w-[300px]">
      <div className="flex-shrink-0 w-10 h-10 bg-background rounded-lg flex items-center justify-center">
        {getFileIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{fileName}</p>
        <p className="text-xs text-muted-foreground">{formatFileSize(fileSize)}</p>
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="h-8 w-8 p-0 flex-shrink-0"
        onClick={handleDownload}
      >
        <Download className="h-4 w-4" />
      </Button>
    </div>
  );
}
